import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import './VimSimulator.css';

// Initial sample text for the buffer
const INITIAL_TEXT = `Welcome to the Vim Simulator!
This is a practice environment to learn Vim commands.

Try these commands:
- Press 'i' to enter INSERT mode
- Press 'Esc' to return to NORMAL mode
- Use h, j, k, l to move the cursor
- Press 'dd' to delete a line
- Press 'yy' to yank (copy) a line
- Press 'p' to paste

Practice makes perfect!
Good luck on your Vim journey.`;

// Practice tasks
const vimTasks = [
  {
    id: 1,
    title: 'Basic Navigation',
    description: 'Move the cursor to line 5 using vim navigation.',
    objective: 'Navigate to line 5',
    checkComplete: (state) => state.cursorLine === 4, // 0-indexed
    hint: 'Use j to move down or type 5G to jump directly',
    xpReward: 15
  },
  {
    id: 2,
    title: 'Enter Insert Mode',
    description: 'Enter INSERT mode to start typing text.',
    objective: 'Enter INSERT mode',
    checkComplete: (state) => state.mode === 'INSERT',
    hint: 'Press i to insert before cursor, or a to append after',
    xpReward: 10
  },
  {
    id: 3,
    title: 'Delete a Line',
    description: 'Delete the current line using the dd command.',
    objective: 'Delete a line with dd',
    checkComplete: (state) => state.lastAction === 'delete-line',
    hint: 'Press d twice quickly to delete the entire line',
    xpReward: 20
  },
  {
    id: 4,
    title: 'Yank and Paste',
    description: 'Copy a line with yy and paste it with p.',
    objective: 'Yank a line and paste it',
    checkComplete: (state) => state.lastAction === 'paste' && state.yankBuffer,
    hint: 'First press yy to yank, then move and press p to paste',
    xpReward: 25
  },
  {
    id: 5,
    title: 'Jump to End of File',
    description: 'Navigate to the last line of the file.',
    objective: 'Jump to the last line',
    checkComplete: (state, buffer) => state.cursorLine === buffer.length - 1,
    hint: 'Press G (capital G) to jump to the last line',
    xpReward: 15
  },
  {
    id: 6,
    title: 'Jump to Beginning',
    description: 'Navigate back to the first line of the file.',
    objective: 'Jump to the first line',
    checkComplete: (state) => state.cursorLine === 0,
    hint: 'Press gg to jump to the first line',
    xpReward: 15
  },
  {
    id: 7,
    title: 'Delete a Character',
    description: 'Delete the character under the cursor.',
    objective: 'Delete a character with x',
    checkComplete: (state) => state.lastAction === 'delete-char',
    hint: 'Press x to delete the character at the cursor',
    xpReward: 10
  },
  {
    id: 8,
    title: 'Undo an Action',
    description: 'Undo the last change you made.',
    objective: 'Undo with u',
    checkComplete: (state) => state.lastAction === 'undo',
    hint: 'Press u to undo the last change',
    xpReward: 15
  }
];

const VimSimulator = () => {
  const { awardXP } = useGame();
  const editorRef = useRef(null);

  // Buffer state (array of lines)
  const [buffer, setBuffer] = useState(INITIAL_TEXT.split('\n'));
  const [history, setHistory] = useState([INITIAL_TEXT.split('\n')]); // For undo
  const [historyIndex, setHistoryIndex] = useState(0);

  // Cursor state
  const [cursorLine, setCursorLine] = useState(0);
  const [cursorCol, setCursorCol] = useState(0);

  // Mode state: NORMAL, INSERT, VISUAL, COMMAND
  const [mode, setMode] = useState('NORMAL');

  // Command buffer for multi-key commands (e.g., dd, yy, 5G)
  const [pendingKeys, setPendingKeys] = useState('');

  // Yank buffer for copy/paste
  const [yankBuffer, setYankBuffer] = useState(null);

  // Command line input for : commands
  const [commandLine, setCommandLine] = useState('');

  // Status message
  const [statusMessage, setStatusMessage] = useState('');

  // Task state
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [lastAction, setLastAction] = useState(null);

  const currentTask = vimTasks[currentTaskIndex];

  // Save state to history for undo
  const saveToHistory = useCallback((newBuffer) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push([...newBuffer]);
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Ensure cursor stays within bounds
  const clampCursor = useCallback((line, col, buf) => {
    const newLine = Math.max(0, Math.min(line, buf.length - 1));
    const lineLength = buf[newLine]?.length || 0;
    const maxCol = mode === 'INSERT' ? lineLength : Math.max(0, lineLength - 1);
    const newCol = Math.max(0, Math.min(col, maxCol));
    return { line: newLine, col: newCol };
  }, [mode]);

  // Show temporary status message
  const showStatus = useCallback((msg, duration = 2000) => {
    setStatusMessage(msg);
    setTimeout(() => setStatusMessage(''), duration);
  }, []);

  // Check if current task is complete
  useEffect(() => {
    if (!currentTask || completedTasks.includes(currentTask.id)) return;

    const state = { mode, cursorLine, cursorCol, lastAction, yankBuffer };
    if (currentTask.checkComplete(state, buffer)) {
      setCompletedTasks(prev => [...prev, currentTask.id]);
      awardXP('intermediate', {
        usedHint: false,
        usedAnswer: false,
        lessonId: `vim-sim-${currentTask.id}`,
        topic: 'vim'
      });
      showStatus(`Task complete! +${currentTask.xpReward} XP`);

      // Move to next task after delay
      setTimeout(() => {
        if (currentTaskIndex < vimTasks.length - 1) {
          setCurrentTaskIndex(prev => prev + 1);
        }
      }, 1500);
    }
  }, [mode, cursorLine, cursorCol, lastAction, yankBuffer, buffer, currentTask, completedTasks, currentTaskIndex, awardXP, showStatus]);

  // Handle key events
  const handleKeyDown = useCallback((e) => {
    // Prevent default for most keys in NORMAL mode
    if (mode !== 'INSERT' && mode !== 'COMMAND') {
      if (!e.ctrlKey && !e.metaKey) {
        e.preventDefault();
      }
    }

    const key = e.key;

    // COMMAND mode (: commands)
    if (mode === 'COMMAND') {
      if (key === 'Escape') {
        setMode('NORMAL');
        setCommandLine('');
        return;
      }
      if (key === 'Enter') {
        executeCommand(commandLine);
        setMode('NORMAL');
        setCommandLine('');
        return;
      }
      if (key === 'Backspace') {
        setCommandLine(prev => prev.slice(0, -1));
        if (commandLine.length <= 1) {
          setMode('NORMAL');
          setCommandLine('');
        }
        return;
      }
      if (key.length === 1) {
        setCommandLine(prev => prev + key);
      }
      return;
    }

    // INSERT mode
    if (mode === 'INSERT') {
      if (key === 'Escape') {
        setMode('NORMAL');
        // Move cursor back one if possible
        const { col } = clampCursor(cursorLine, cursorCol - 1, buffer);
        setCursorCol(col);
        return;
      }

      if (key === 'Backspace') {
        e.preventDefault();
        if (cursorCol > 0) {
          const newBuffer = [...buffer];
          const line = newBuffer[cursorLine];
          newBuffer[cursorLine] = line.slice(0, cursorCol - 1) + line.slice(cursorCol);
          setBuffer(newBuffer);
          setCursorCol(cursorCol - 1);
          saveToHistory(newBuffer);
        } else if (cursorLine > 0) {
          // Join with previous line
          const newBuffer = [...buffer];
          const prevLineLen = newBuffer[cursorLine - 1].length;
          newBuffer[cursorLine - 1] += newBuffer[cursorLine];
          newBuffer.splice(cursorLine, 1);
          setBuffer(newBuffer);
          setCursorLine(cursorLine - 1);
          setCursorCol(prevLineLen);
          saveToHistory(newBuffer);
        }
        return;
      }

      if (key === 'Enter') {
        e.preventDefault();
        const newBuffer = [...buffer];
        const line = newBuffer[cursorLine];
        newBuffer[cursorLine] = line.slice(0, cursorCol);
        newBuffer.splice(cursorLine + 1, 0, line.slice(cursorCol));
        setBuffer(newBuffer);
        setCursorLine(cursorLine + 1);
        setCursorCol(0);
        saveToHistory(newBuffer);
        return;
      }

      // Regular character input
      if (key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const newBuffer = [...buffer];
        const line = newBuffer[cursorLine];
        newBuffer[cursorLine] = line.slice(0, cursorCol) + key + line.slice(cursorCol);
        setBuffer(newBuffer);
        setCursorCol(cursorCol + 1);
        saveToHistory(newBuffer);
        return;
      }
      return;
    }

    // NORMAL mode
    if (mode === 'NORMAL') {
      // Handle Escape - clear pending keys
      if (key === 'Escape') {
        setPendingKeys('');
        return;
      }

      // Start command mode
      if (key === ':') {
        setMode('COMMAND');
        setCommandLine(':');
        return;
      }

      // Combine with pending keys
      const fullCommand = pendingKeys + key;

      // Check for number prefix (for commands like 5j, 10G)
      const numberMatch = fullCommand.match(/^(\d+)(.*)$/);
      let count = 1;
      let command = fullCommand;
      if (numberMatch && numberMatch[2]) {
        count = parseInt(numberMatch[1], 10);
        command = numberMatch[2];
      } else if (numberMatch && !numberMatch[2]) {
        // Just numbers so far, keep accumulating
        setPendingKeys(fullCommand);
        return;
      }

      // Process commands
      let handled = true;

      switch (command) {
        // Navigation
        case 'h':
          setCursorCol(prev => Math.max(0, prev - count));
          break;
        case 'j':
          setCursorLine(prev => {
            const newLine = Math.min(buffer.length - 1, prev + count);
            const { col } = clampCursor(newLine, cursorCol, buffer);
            setCursorCol(col);
            return newLine;
          });
          break;
        case 'k':
          setCursorLine(prev => {
            const newLine = Math.max(0, prev - count);
            const { col } = clampCursor(newLine, cursorCol, buffer);
            setCursorCol(col);
            return newLine;
          });
          break;
        case 'l':
          setCursorCol(prev => {
            const maxCol = Math.max(0, (buffer[cursorLine]?.length || 1) - 1);
            return Math.min(maxCol, prev + count);
          });
          break;

        // Line navigation
        case '0':
          if (pendingKeys === '') {
            setCursorCol(0);
          } else {
            handled = false;
          }
          break;
        case '$':
          setCursorCol(Math.max(0, (buffer[cursorLine]?.length || 1) - 1));
          break;
        case 'G':
          if (numberMatch && numberMatch[1]) {
            // Go to specific line
            const targetLine = Math.min(count - 1, buffer.length - 1);
            setCursorLine(Math.max(0, targetLine));
          } else {
            // Go to last line
            setCursorLine(buffer.length - 1);
          }
          setCursorCol(0);
          break;
        case 'g':
          setPendingKeys('g');
          handled = false;
          break;
        case 'gg':
          setCursorLine(0);
          setCursorCol(0);
          break;

        // Insert mode
        case 'i':
          setMode('INSERT');
          showStatus('-- INSERT --');
          break;
        case 'a':
          setMode('INSERT');
          setCursorCol(prev => Math.min(prev + 1, buffer[cursorLine]?.length || 0));
          showStatus('-- INSERT --');
          break;
        case 'A':
          setMode('INSERT');
          setCursorCol(buffer[cursorLine]?.length || 0);
          showStatus('-- INSERT --');
          break;
        case 'o':
          {
            const newBuffer = [...buffer];
            newBuffer.splice(cursorLine + 1, 0, '');
            setBuffer(newBuffer);
            setCursorLine(cursorLine + 1);
            setCursorCol(0);
            setMode('INSERT');
            saveToHistory(newBuffer);
            showStatus('-- INSERT --');
          }
          break;
        case 'O':
          {
            const newBuffer = [...buffer];
            newBuffer.splice(cursorLine, 0, '');
            setBuffer(newBuffer);
            setCursorCol(0);
            setMode('INSERT');
            saveToHistory(newBuffer);
            showStatus('-- INSERT --');
          }
          break;

        // Delete
        case 'x':
          {
            const newBuffer = [...buffer];
            const line = newBuffer[cursorLine];
            if (line.length > 0) {
              newBuffer[cursorLine] = line.slice(0, cursorCol) + line.slice(cursorCol + 1);
              setBuffer(newBuffer);
              const { col } = clampCursor(cursorLine, cursorCol, newBuffer);
              setCursorCol(col);
              saveToHistory(newBuffer);
              setLastAction('delete-char');
              showStatus('Deleted character');
            }
          }
          break;
        case 'd':
          setPendingKeys('d');
          handled = false;
          break;
        case 'dd':
          {
            const newBuffer = [...buffer];
            const deletedLine = newBuffer.splice(cursorLine, count);
            if (newBuffer.length === 0) newBuffer.push('');
            setYankBuffer(deletedLine.join('\n'));
            setBuffer(newBuffer);
            const { line } = clampCursor(cursorLine, 0, newBuffer);
            setCursorLine(line);
            setCursorCol(0);
            saveToHistory(newBuffer);
            setLastAction('delete-line');
            showStatus(`${count} line(s) deleted`);
          }
          break;

        // Yank (copy)
        case 'y':
          setPendingKeys('y');
          handled = false;
          break;
        case 'yy':
          {
            const lines = buffer.slice(cursorLine, cursorLine + count);
            setYankBuffer(lines.join('\n'));
            setLastAction('yank');
            showStatus(`${count} line(s) yanked`);
          }
          break;

        // Paste
        case 'p':
          if (yankBuffer) {
            const newBuffer = [...buffer];
            const lines = yankBuffer.split('\n');
            newBuffer.splice(cursorLine + 1, 0, ...lines);
            setBuffer(newBuffer);
            setCursorLine(cursorLine + 1);
            setCursorCol(0);
            saveToHistory(newBuffer);
            setLastAction('paste');
            showStatus('Pasted');
          }
          break;
        case 'P':
          if (yankBuffer) {
            const newBuffer = [...buffer];
            const lines = yankBuffer.split('\n');
            newBuffer.splice(cursorLine, 0, ...lines);
            setBuffer(newBuffer);
            setCursorCol(0);
            saveToHistory(newBuffer);
            setLastAction('paste');
            showStatus('Pasted');
          }
          break;

        // Undo/Redo
        case 'u':
          if (historyIndex > 0) {
            setHistoryIndex(prev => prev - 1);
            setBuffer([...history[historyIndex - 1]]);
            setLastAction('undo');
            showStatus('Undo');
          }
          break;

        default:
          if (e.ctrlKey && key === 'r') {
            // Redo
            if (historyIndex < history.length - 1) {
              setHistoryIndex(prev => prev + 1);
              setBuffer([...history[historyIndex + 1]]);
              setLastAction('redo');
              showStatus('Redo');
            }
          } else {
            handled = false;
          }
      }

      if (handled) {
        setPendingKeys('');
      }
    }
  }, [mode, buffer, cursorLine, cursorCol, pendingKeys, yankBuffer, commandLine, history, historyIndex, clampCursor, saveToHistory, showStatus]);

  // Execute : commands
  const executeCommand = (cmd) => {
    const command = cmd.slice(1).trim(); // Remove the :

    if (command === 'w') {
      showStatus('File written (simulated)');
    } else if (command === 'q') {
      showStatus('Cannot quit simulator - use back button');
    } else if (command === 'wq' || command === 'x') {
      showStatus('File written (simulated)');
    } else if (command.match(/^\d+$/)) {
      // Jump to line number
      const lineNum = parseInt(command, 10) - 1;
      setCursorLine(Math.max(0, Math.min(lineNum, buffer.length - 1)));
      setCursorCol(0);
    } else if (command.startsWith('set ')) {
      showStatus(`Set: ${command.slice(4)}`);
    } else {
      showStatus(`Unknown command: ${command}`);
    }
  };

  // Focus editor on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  }, []);

  // Re-focus editor when clicking on it
  const handleEditorClick = () => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  return (
    <div className="vim-simulator">
      <div className="vim-header">
        <Link to="/topics" className="back-link">← Back to Topics</Link>
        <h1>Interactive Vim Simulator</h1>
        <p>Practice Vim commands in a safe environment</p>
      </div>

      <div className="vim-layout">
        {/* Task Panel */}
        <div className="vim-task-panel">
          <h3>Current Task</h3>
          {currentTask ? (
            <div className="vim-task-card">
              <div className="task-number">Task {currentTaskIndex + 1} of {vimTasks.length}</div>
              <h4>{currentTask.title}</h4>
              <p>{currentTask.description}</p>
              <div className="task-objective">
                <strong>Objective:</strong> {currentTask.objective}
              </div>
              <div className="task-hint">
                <strong>Hint:</strong> {currentTask.hint}
              </div>
              <div className="task-xp">+{currentTask.xpReward} XP</div>
            </div>
          ) : (
            <div className="vim-task-complete">
              <span>🎉</span>
              <p>All tasks completed!</p>
            </div>
          )}

          <div className="completed-tasks">
            <h4>Completed Tasks</h4>
            <ul>
              {vimTasks.map((task, idx) => (
                <li key={task.id} className={completedTasks.includes(task.id) ? 'completed' : ''}>
                  {completedTasks.includes(task.id) ? '✓' : '○'} {task.title}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Editor */}
        <div className="vim-editor-container">
          <div
            ref={editorRef}
            className="vim-editor"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onClick={handleEditorClick}
          >
            {/* Line numbers and content */}
            <div className="vim-lines">
              {buffer.map((line, lineIdx) => (
                <div
                  key={lineIdx}
                  className={`vim-line ${lineIdx === cursorLine ? 'current-line' : ''}`}
                >
                  <span className="line-number">{lineIdx + 1}</span>
                  <span className="line-content">
                    {line.split('').map((char, charIdx) => (
                      <span
                        key={charIdx}
                        className={
                          lineIdx === cursorLine && charIdx === cursorCol
                            ? `cursor cursor-${mode.toLowerCase()}`
                            : ''
                        }
                      >
                        {char}
                      </span>
                    ))}
                    {/* Cursor at end of line or empty line */}
                    {lineIdx === cursorLine && cursorCol >= line.length && (
                      <span className={`cursor cursor-${mode.toLowerCase()}`}>
                        {mode === 'INSERT' ? '|' : ' '}
                      </span>
                    )}
                    {line.length === 0 && lineIdx !== cursorLine && <span>&nbsp;</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Status bar */}
          <div className="vim-status-bar">
            <div className="vim-mode">
              <span className={`mode-indicator mode-${mode.toLowerCase()}`}>
                -- {mode} --
              </span>
            </div>
            <div className="vim-info">
              {statusMessage || (
                <>
                  <span>Ln {cursorLine + 1}, Col {cursorCol + 1}</span>
                  {pendingKeys && <span className="pending-keys">{pendingKeys}</span>}
                </>
              )}
            </div>
          </div>

          {/* Command line */}
          {mode === 'COMMAND' && (
            <div className="vim-command-line">
              <span>{commandLine}</span>
              <span className="command-cursor">|</span>
            </div>
          )}
        </div>

        {/* Quick Reference */}
        <div className="vim-reference">
          <h3>Quick Reference</h3>
          <div className="reference-section">
            <h4>Modes</h4>
            <ul>
              <li><kbd>i</kbd> Insert mode</li>
              <li><kbd>Esc</kbd> Normal mode</li>
              <li><kbd>:</kbd> Command mode</li>
            </ul>
          </div>
          <div className="reference-section">
            <h4>Navigation</h4>
            <ul>
              <li><kbd>h j k l</kbd> Left/Down/Up/Right</li>
              <li><kbd>gg</kbd> Go to first line</li>
              <li><kbd>G</kbd> Go to last line</li>
              <li><kbd>0</kbd> Start of line</li>
              <li><kbd>$</kbd> End of line</li>
            </ul>
          </div>
          <div className="reference-section">
            <h4>Editing</h4>
            <ul>
              <li><kbd>x</kbd> Delete character</li>
              <li><kbd>dd</kbd> Delete line</li>
              <li><kbd>yy</kbd> Yank (copy) line</li>
              <li><kbd>p</kbd> Paste after</li>
              <li><kbd>u</kbd> Undo</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VimSimulator;
